"use client";

import Link from "next/link";
import Script from "next/script";
import React, { useState } from "react";

interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare let grecaptcha: Grecaptcha;

interface QuickContactFormProps {
  formConfig?: {
    showHeader?: boolean;
    header?: {
      leftIcon?: string;
      leftText?: string;
      leftTextColor?: string;
      rightText?: string;
      rightTextColor?: string;
      dotColor?: string;
      leftBackgroundColor?: string;
      rightBackgroundColor?: string;
    };
    formTitle?: string;
    titleColor?: string;
    fields?: Array<{
      fieldType: string;
      enabled?: boolean;
    }>;
    eligibilityCheckboxText?: string;
    services?: Array<{
      label?: string;
      value?: string;
    }>;
    buttonText?: string;
    footerMessage?: {
      icon?: string;
      text?: string;
      textColor?: string;
      backgroundColor?: string;
    };
    privacyTextColor?: string;
    emailSettings?: {
      recipientEmail?: string;
      adminSubject?: string;
      adminBody?: string;
      visitorSubject?: string;
      visitorBody?: string;
    };
    backgroundColor?: string;
    customBackgroundColor?: string;
    successMessage?: string;
    successMessageColor?: string;
  };
}

export default function QuickContactForm({
  formConfig,
}: QuickContactFormProps = {}) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    services: "",
    age: "",
    hairLossLevel: "",
  });
  const [eligibilityAccepted, setEligibilityAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showTerms, setShowTerms] = useState(false);

  const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";

  // Default values
  const showHeader = formConfig?.showHeader !== false;
  const header = formConfig?.header || {};
  const leftIcon = header.leftIcon || "";
  const leftText = header.leftText || "Consultation";
  const leftTextColor = header.leftTextColor || "";
  const rightText = header.rightText || "Book a consultation";
  const rightTextColor = header.rightTextColor || "";
  const dotColor = header.dotColor || "#ef4444";
  const formTitle =
    formConfig?.formTitle || "Book a consultation";
  
  // Convert array-based fields to boolean flags for backward compatibility
  const fieldsArray = formConfig?.fields || [];
  const getFieldEnabled = (fieldType: string): boolean => {
    const field = fieldsArray.find((f) => f.fieldType === fieldType);
    // Field must exist in array AND be enabled (or enabled not explicitly false)
    return field !== undefined && field.enabled !== false;
  };
  
  const showName = getFieldEnabled("name");
  const showPhone = getFieldEnabled("phone");
  const showEmail = getFieldEnabled("email");
  const showCity = getFieldEnabled("city");
  const showServices = getFieldEnabled("services");
  const showAge = getFieldEnabled("age");
  const showHairLossLevel = getFieldEnabled("hairLossLevel");
  const showEligibilityCheckbox = getFieldEnabled("eligibilityCheckbox");
  
  // Get field order from array (maintain order for rendering)
  const fieldOrder = fieldsArray.map((f) => f.fieldType);
  const eligibilityCheckboxText =
    formConfig?.eligibilityCheckboxText ||
    "I understand that suitability for any treatment is decided only after a medical consultation.";
  const services = formConfig?.services || [];
  const buttonText = formConfig?.buttonText || "Call me back";
  const footerMessage = formConfig?.footerMessage || {};
  const footerIcon = footerMessage.icon || "";
  const footerText = footerMessage.text || "Our clinic team will call you back during working hours.";
  const footerTextColor = footerMessage.textColor || "#10b981";
  const backgroundColor = formConfig?.backgroundColor || "gradient";
  const customBgColor = formConfig?.customBackgroundColor || "#3b82f6";
  const titleColor = formConfig?.titleColor || "";
  const privacyTextColor = formConfig?.privacyTextColor || "";
  const defaultSuccessMessage =
    formConfig?.successMessage ||
    "Thank you! We've received your appointment request. Our team will contact you shortly to confirm your consultation.";
  const successMessageColor = formConfig?.successMessageColor || "";

  // Determine text color based on background
  const isLightBackground =
    backgroundColor === "custom" &&
    (customBgColor.toLowerCase() === "#ffffff" ||
      customBgColor.toLowerCase() === "#fff" ||
      customBgColor.toLowerCase() === "white");

  const textColor = isLightBackground ? "text-gray-900" : "text-white";

  // Custom title color or auto
  const titleColorClass = titleColor ? "" : textColor;
  const titleColorStyle = titleColor ? { color: titleColor } : undefined;

  // Custom privacy text color or auto
  const privacyColorClass = privacyTextColor
    ? ""
    : isLightBackground
      ? "text-gray-600"
      : "text-white/90";
  const privacyColorStyle = privacyTextColor
    ? { color: privacyTextColor }
    : undefined;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEligibilityAccepted(e.target.checked);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("Form submit triggered", { formData, eligibilityAccepted });
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    // Validate eligibility checkbox if shown
    if (showEligibilityCheckbox && !eligibilityAccepted) {
      setErrorMessage("Please accept the eligibility criteria to continue.");
      setLoading(false);
      return;
    }

    // Check if reCAPTCHA script is loaded
    if (typeof window === "undefined" || typeof grecaptcha === "undefined") {
      console.error("reCAPTCHA client is not available.");
      setErrorMessage("reCAPTCHA client is not ready. Please try again later.");
      setLoading(false);
      return;
    }

    // Check if SITE_KEY is available
    if (!SITE_KEY) {
      console.error("reCAPTCHA site key is not configured.");
      setErrorMessage("Form configuration error. Please contact support.");
      setLoading(false);
      return;
    }

    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(SITE_KEY, {
          action: "submit",
        });

        const recipientEmail =
          formConfig?.emailSettings?.recipientEmail || "trichosleads@gmail.com";

        // Prepare custom email templates if provided
        const customTemplates =
          formConfig?.emailSettings?.adminSubject ||
          formConfig?.emailSettings?.adminBody ||
          formConfig?.emailSettings?.visitorSubject ||
          formConfig?.emailSettings?.visitorBody
            ? {
                adminSubject: formConfig.emailSettings.adminSubject,
                adminBody: formConfig.emailSettings.adminBody,
                visitorSubject: formConfig.emailSettings.visitorSubject,
                visitorBody: formConfig.emailSettings.visitorBody,
              }
            : undefined;

        // Determine if visitor email should be sent (only if email field is enabled and email is provided)
        const sendVisitorEmail = showEmail && formData.email;

        const response = await fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData,
            eligibilityAccepted: showEligibilityCheckbox ? eligibilityAccepted : undefined,
            token,
            formType: "quickContact",
            recipientEmail,
            customTemplates,
            sendVisitorEmail,
          }),
        });

        if (response.ok) {
          await response.json();
          setSuccessMessage(defaultSuccessMessage);
          setSubmitted(true);
          setErrorMessage("");

          // GTM event tracking
          if (typeof window !== "undefined" && window.dataLayer) {
            window.dataLayer.push({
              event: "trichos_form_submit",
              formType: "quick_contact",
              formId: "trichos-quick-contact-form",
              formName: "Quick Contact Form",
              submissionStatus: "success",
            });

            // Google Ads conversion tracking for High Intent - Consultation Form
            // Conversion ID: 967623456, Conversion Label: KbnZCMC9xoUbEKCGs80D
            // Event name: "quick_contact_conversion" - Use this in GTM trigger
            window.dataLayer.push({
              event: "quick_contact_conversion",
              send_to: "AW-967623456/KbnZCMC9xoUbEKCGs80D",
              conversion_id: "967623456",
              conversion_label: "KbnZCMC9xoUbEKCGs80D",
            });
          }

          // Redirect to thank you page after a short delay
          setTimeout(() => {
            window.location.href = "/thank-you-consultation";
          }, 1000);
        } else {
          const errorData = await response.json();
          setErrorMessage(errorData.message || "Error submitting form.");
          setSuccessMessage("");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        const errorMsg = error instanceof Error ? error.message : "An error occurred. Please try again.";
        setErrorMessage(errorMsg);
        setSuccessMessage("");
        setLoading(false);
      }
    });
  };

  const handleReset = () => {
    setFormData({
      name: "",
      phone: "",
      email: "",
      city: "",
      services: "",
      age: "",
      hairLossLevel: "",
    });
    setEligibilityAccepted(false);
    setSubmitted(false);
    setSuccessMessage("");
    setErrorMessage("");
  };

  return (
    <>
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`}
        strategy="lazyOnload"
      />
      <div className="mx-auto max-w-2xl px-2 py-3">
        {/* Outer Container - All three parts connected with shared border */}
        <div
          className="overflow-hidden rounded-xl"
          style={
            backgroundColor === "gradient"
              ? {
                  background:
                    "linear-gradient(to bottom right, #fb923c, #ec4899)",
                }
              : { backgroundColor: customBgColor }
          }
        >
          {/* Part 1: Header Section - Two Parts */}
          {showHeader && (
            <div className="flex border-b-[0.5px] border-white/20">
              {/* Left Part */}
              <div
                className="flex-1 px-4 py-2"
                style={
                  header.leftBackgroundColor
                    ? { backgroundColor: header.leftBackgroundColor }
                    : undefined
                }
              >
                <div className="flex items-center gap-2">
                  {leftIcon && (
                    <span className="text-base">
                      {leftIcon.startsWith("http") ? (
                        <img src={leftIcon} alt="" className="size-4" />
                      ) : (
                        leftIcon
                      )}
                    </span>
                  )}
                  <div
                    className={`text-sm font-bold ${leftTextColor ? "" : textColor}`}
                    style={leftTextColor ? { color: leftTextColor } : undefined}
                  >
                    {leftText}
                  </div>
                </div>
              </div>
              {/* Right Part - Only as wide as content */}
              <div
                className="flex flex-none items-center px-4 py-2"
                style={
                  header.rightBackgroundColor
                    ? { backgroundColor: header.rightBackgroundColor }
                    : undefined
                }
              >
                <div className="flex items-center gap-2">
                  <span className="relative flex size-2">
                    <span
                      className="absolute inline-flex size-full animate-ping rounded-full opacity-75"
                      style={{ backgroundColor: dotColor }}
                    ></span>
                    <span
                      className="relative inline-flex size-2 rounded-full"
                      style={{ backgroundColor: dotColor }}
                    ></span>
                  </span>
                  <div
                    className={`text-sm font-bold ${rightTextColor ? "" : textColor}`}
                    style={
                      rightTextColor ? { color: rightTextColor } : undefined
                    }
                  >
                    {rightText}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Part 2: Form Section */}
          <div className="p-4">
            {submitted ? (
              <div className="text-center">
                {successMessage && (
                  <p
                    className={`text-xl font-semibold ${successMessageColor ? "" : textColor}`}
                    style={
                      successMessageColor ? { color: successMessageColor } : undefined
                    }
                  >
                    {successMessage}
                  </p>
                )}
                {errorMessage && (
                  <p
                    className={`text-xl font-semibold ${isLightBackground ? "text-red-600" : "text-red-900"}`}
                  >
                    {errorMessage}
                  </p>
                )}
                <button
                  onClick={handleReset}
                  className="mt-4 rounded-2xl border border-black bg-black px-6 py-2 font-bold text-white transition-colors hover:bg-neutral-900"
                >
                  Reset Form
                </button>
              </div>
            ) : (
              <>
                {errorMessage && (
                  <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3">
                    <p className={`text-sm font-semibold ${isLightBackground ? "text-red-600" : "text-red-900"}`}>
                      {errorMessage}
                    </p>
                  </div>
                )}
                <form onSubmit={handleSubmit} id="trichos-quick-contact-form" noValidate>
                <h2
                  className={`mb-3 text-center text-sm font-bold uppercase tracking-wider ${titleColorClass}`}
                  style={titleColorStyle}
                >
                  {formTitle}
                </h2>
                <div className="mb-3 space-y-2.5">
                  {(() => {
                    // Fields that should be in two-column layout
                    const twoColumnFields = ["name", "phone", "email", "city", "age", "hairLossLevel"];
                    // Fields that should be full width
                    const fullWidthFields = ["services", "eligibilityCheckbox"];
                    
                    // Filter enabled fields and maintain order
                    const enabledFields = fieldsArray.filter((f) => f.enabled !== false);
                    
                    // Group fields for rendering
                    const fieldElements: React.ReactElement[] = [];
                    let currentGroup: React.ReactElement[] = [];
                    
                    enabledFields.forEach((field, index) => {
                      const fieldElement = renderField(field.fieldType);
                      if (!fieldElement) return;
                      
                      if (twoColumnFields.includes(field.fieldType)) {
                        // Add to current two-column group
                        currentGroup.push(fieldElement);
                        
                        // If group has 2 items or next field is not two-column, render group
                        const nextField = enabledFields[index + 1];
                        if (
                          currentGroup.length === 2 ||
                          !nextField ||
                          !twoColumnFields.includes(nextField.fieldType)
                        ) {
                          fieldElements.push(
                            <div key={`group-${index}`} className="flex flex-col gap-4 md:flex-row">
                              {currentGroup}
                            </div>
                          );
                          currentGroup = [];
                        }
                      } else {
                        // Full width field - render any pending group first
                        if (currentGroup.length > 0) {
                          fieldElements.push(
                            <div key={`group-before-${index}`} className="flex flex-col gap-4 md:flex-row">
                              {currentGroup}
                            </div>
                          );
                          currentGroup = [];
                        }
                        fieldElements.push(fieldElement);
                      }
                    });
                    
                    // Render any remaining group
                    if (currentGroup.length > 0) {
                      fieldElements.push(
                        <div key="group-final" className="flex flex-col gap-4 md:flex-row">
                          {currentGroup}
                        </div>
                      );
                    }
                    
                    return fieldElements;
                    
                    function renderField(fieldType: string): React.ReactElement | null {
                      switch (fieldType) {
                        case "name":
                          return (
                            <div key="name" className="flex-1">
                              <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full rounded-xl border border-gray-200/50 bg-white/95 p-2 text-sm text-gray-900 shadow-sm focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-white"
                                placeholder="Name"
                              />
                            </div>
                          );
                        case "phone":
                          return (
                            <div key="phone" className="flex-1">
                              <input
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                                className="w-full rounded-xl border border-gray-200/50 bg-white/95 p-2 text-sm text-gray-900 shadow-sm focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-white"
                                placeholder="Phone Number"
                              />
                            </div>
                          );
                        case "email":
                          return (
                            <div key="email" className="flex-1">
                              <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full rounded-xl border border-gray-200/50 bg-white/95 p-2 text-sm text-gray-900 shadow-sm focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-white"
                                placeholder="Enter your email"
                              />
                            </div>
                          );
                        case "city":
                          return (
                            <div key="city" className="flex-1">
                              <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                required
                                className="w-full rounded-xl border border-gray-200/50 bg-white/95 p-2 text-sm text-gray-900 shadow-sm focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-white"
                                placeholder="Enter your city"
                              />
                            </div>
                          );
                        case "services":
                          return (
                            <div key="services">
                              <select
                                name="services"
                                value={formData.services}
                                onChange={handleChange}
                                required
                                className="w-full rounded-xl border border-gray-200/50 bg-white/95 p-2 text-sm text-gray-900 shadow-sm focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-white"
                              >
                                <option value="">Select a service</option>
                                {services.length > 0 ? (
                                  services.map((service, idx) => (
                                    <option
                                      key={idx}
                                      value={service.value || service.label}
                                    >
                                      {service.label || service.value}
                                    </option>
                                  ))
                                ) : (
                                  <>
                                    <option value="hair-transplant">
                                      Hair Transplant
                                    </option>
                                    <option value="prp-therapy">PRP Therapy</option>
                                    <option value="hair-restoration">
                                      Hair Restoration
                                    </option>
                                    <option value="scalp-treatment">
                                      Scalp Treatment
                                    </option>
                                    <option value="consultation">Consultation</option>
                                  </>
                                )}
                              </select>
                            </div>
                          );
                        case "age":
                          return (
                            <div key="age" className="flex-1">
                              <input
                                type="number"
                                name="age"
                                value={formData.age}
                                onChange={handleChange}
                                required
                                min="1"
                                max="120"
                                className="w-full rounded-xl border border-gray-200/50 bg-white/95 p-2 text-sm text-gray-900 shadow-sm focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-white"
                                placeholder="Age"
                              />
                            </div>
                          );
                        case "hairLossLevel":
                          return (
                            <div key="hairLossLevel" className="flex-1">
                              <select
                                name="hairLossLevel"
                                value={formData.hairLossLevel}
                                onChange={handleChange}
                                required
                                className="w-full rounded-xl border border-gray-200/50 bg-white/95 p-2 text-sm text-gray-900 shadow-sm focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-white"
                              >
                                <option value="">Select Hair Loss Level</option>
                                <option value="mild">Mild</option>
                                <option value="moderate">Moderate</option>
                                <option value="Advanced">Advanced</option>
                              </select>
                            </div>
                          );
                        case "eligibilityCheckbox":
                          return (
                            <div key="eligibilityCheckbox" className="flex items-start gap-2">
                              <input
                                type="checkbox"
                                id="eligibility-checkbox"
                                checked={eligibilityAccepted}
                                onChange={handleCheckboxChange}
                                required
                                className="mt-1 size-4 cursor-pointer rounded border-gray-300 text-violet-950 focus:ring-2 focus:ring-violet-950"
                              />
                              <label
                                htmlFor="eligibility-checkbox"
                                className="cursor-pointer text-xs text-gray-900"
                              >
                                {eligibilityCheckboxText}
                              </label>
                            </div>
                          );
                        default:
                          return null;
                      }
                    }
                  })()}
                </div>

                <div className="flex justify-center">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`inline-flex items-center rounded-full border px-4 py-1.5 text-sm font-bold transition-all ${
                      loading
                        ? "cursor-not-allowed border-gray-400 bg-gray-400 text-gray-600"
                        : "border-black bg-black text-white shadow-lg hover:bg-neutral-900 hover:shadow-xl"
                    }`}
                  >
                    {loading ? (
                      <svg
                        className="mx-auto size-5 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        ></path>
                      </svg>
                    ) : (
                      buttonText
                    )}
                  </button>
                </div>
              </form>
              </>
            )}
          </div>

          {/* Part 3: Footer Section */}
          <div
            className="relative border-t-[0.5px] border-white/20 px-4 py-2"
            style={
              footerMessage.backgroundColor
                ? { backgroundColor: footerMessage.backgroundColor }
                : undefined
            }
          >
            {/* Footer Message and Terms Button Row */}
            <div className="flex flex-col items-center gap-2 md:relative md:flex-row">
              {footerText && (
                <div className="flex flex-1 items-center justify-center gap-2">
                  {footerIcon && (
                    <span className="text-base">
                      {footerIcon.startsWith("http") ? (
                        <img src={footerIcon} alt="" className="size-4" />
                      ) : (
                        footerIcon
                      )}
                    </span>
                  )}
                  <p
                    className="text-sm font-bold"
                    style={{ color: footerTextColor }}
                  >
                    {footerText}
                  </p>
                </div>
              )}
              {/* Terms Button - Right Corner on Desktop, Centered on Mobile */}
              <div className="md:absolute md:right-0">
                <button
                  type="button"
                  onClick={() => setShowTerms(!showTerms)}
                  className={`flex items-center gap-1 text-[10px] ${privacyColorClass}`}
                  style={privacyColorStyle}
                >
                  Terms
                  <svg
                    className={`size-3 transition-transform ${showTerms ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
            </div>
            {/* Terms Text - Expandable */}
            {showTerms && (
              <div className="mt-2 w-full">
                <p
                  className={`text-center text-[10px] ${privacyColorClass}`}
                  style={privacyColorStyle}
                >
                  By submitting, I agree to be contacted by Trichos. Protected
                  by{" "}
                  <Link
                    className={`font-semibold underline ${privacyTextColor ? "hover:opacity-80" : isLightBackground ? "hover:text-gray-900" : "hover:text-white"}`}
                    href="https://policies.google.com/privacy"
                    style={
                      privacyTextColor ? { color: privacyTextColor } : undefined
                    }
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    className={`font-semibold underline ${privacyTextColor ? "hover:opacity-80" : isLightBackground ? "hover:text-gray-900" : "hover:text-white"}`}
                    href="https://policies.google.com/terms"
                    style={
                      privacyTextColor ? { color: privacyTextColor } : undefined
                    }
                  >
                    Terms
                  </Link>
                  .
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
